import type { BuildingStructureBox } from "@/utils/supabase";

/**
 * 건물 프리셋 정의
 * mesh_index를 키로 사용하여 건물 구조를 정의합니다.
 *
 * 그리드 셀 경계: CELL_SIZE(5) - ROAD_WIDTH(1.5) = 3.5
 * HALF_SIZE = 1.75
 *
 * 각 Box는 position(상대 위치)과 scale(크기)을 가집니다.
 * position: [x, y, z] - 건물 중심 기준 상대 좌표
 * scale: [width, height, depth] - 박스 크기
 *
 * 제약: position ± scale/2 가 [-1.75, 1.75] 범위 내여야 함
 */

export interface BuildingPreset {
  meshIndex: number;
  name: string;
  description: string;
  structure: BuildingStructureBox[];
}

/**
 * 건물 프리셋 (경계 내로 조정됨)
 * 모든 박스는 position ± scale/2 가 [-1.75, 1.75] 범위 이내
 */
export const BUILDING_PRESETS: BuildingPreset[] = [
  // 2: ㄱ자 건물 (경계 내로 조정)
  {
    meshIndex: 2,
    name: "코너 빌딩",
    description: "ㄱ자 형태의 코너 건물",
    structure: [
      { position: [0.5, 1.5, -0.5], scale: [1.5, 3, 1] },
      { position: [-0.5, 1.5, 0.75], scale: [1, 3, 1.5] },
    ],
  },

  // 3: ㄷ자 건물 (경계 내로 조정)
  {
    meshIndex: 3,
    name: "U자 빌딩",
    description: "ㄷ자 형태의 열린 건물",
    structure: [
      { position: [-0.75, 1, 0], scale: [0.8, 2, 2.5] },
      { position: [0.75, 1, 0], scale: [0.8, 2, 2.5] },
      { position: [0, 1, -1], scale: [0.7, 2, 0.8] },
    ],
  },

  // 6: T자 건물 (경계 내로 조정)
  {
    meshIndex: 6,
    name: "T자 빌딩",
    description: "T자 형태의 건물",
    structure: [
      { position: [0, 1, -0.75], scale: [2.5, 2, 1] },
      { position: [0, 1, 0.75], scale: [1, 2, 1.5] },
    ],
  },

  // 7: 십자 건물 (경계 내로 조정)
  {
    meshIndex: 7,
    name: "크로스 빌딩",
    description: "십자 형태의 건물",
    structure: [
      { position: [0, 1.5, 0], scale: [0.8, 3, 2.5] },
      { position: [-0.9, 1.5, 0], scale: [1, 3, 0.8] },
      { position: [0.9, 1.5, 0], scale: [1, 3, 0.8] },
    ],
  },

  // 8: 높낮이 조합 (경계 내로 조정)
  {
    meshIndex: 8,
    name: "믹스 타워",
    description: "높이가 다른 박스들의 조합",
    structure: [
      { position: [-0.6, 1, 0], scale: [1.2, 2, 1.5] },
      { position: [0.6, 2, 0], scale: [1.2, 4, 1.5] },
    ],
  },

  // 9: L자 높은 건물 (경계 내로 조정)
  {
    meshIndex: 9,
    name: "엘 타워",
    description: "L자 형태의 높은 건물",
    structure: [
      { position: [-0.5, 2, 0], scale: [1.5, 4, 1.5] },
      { position: [0.75, 1, 0], scale: [1.2, 2, 1.5] },
    ],
  },

  // 10: 중앙 타워 + 날개 (경계 내로 조정)
  {
    meshIndex: 10,
    name: "윙 타워",
    description: "중앙 타워와 양쪽 날개",
    structure: [
      { position: [0, 2, 0], scale: [1.2, 4, 1.2] },
      { position: [-1.1, 0.5, 0], scale: [1, 1, 1.5] },
      { position: [1.1, 0.5, 0], scale: [1, 1, 1.5] },
    ],
  },
];

/**
 * mesh_index로 건물 프리셋 가져오기
 */
export function getBuildingPreset(meshIndex: number): BuildingPreset {
  const preset = BUILDING_PRESETS.find((p) => p.meshIndex === meshIndex);
  if (!preset) {
    // 기본값: 첫 번째 프리셋
    return BUILDING_PRESETS[0];
  }
  return preset;
}

/**
 * mesh_index로 건물 구조 가져오기
 */
export function getBuildingStructure(
  meshIndex: number
): BuildingStructureBox[] {
  return getBuildingPreset(meshIndex).structure;
}

/**
 * 사용 가능한 모든 프리셋 목록
 */
export function getAllBuildingPresets(): BuildingPreset[] {
  return BUILDING_PRESETS;
}
